import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { talentsApi, TALENT_CATEGORIES, CATEGORY_LABEL, SOCIAL_SHORT } from '../services/talentsApi';
import './Talents.css';

const TourismeCulturel = () => {
  const [filter, setFilter] = useState('all');
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.tal-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  useEffect(() => {
    let cancelled = false;
    talentsApi.list({ limit: 200 })
      .then((data) => { if (!cancelled) { setTalents(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setTalents([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const c = { all: talents.length };
    for (const t of talents) c[t.category] = (c[t.category] || 0) + 1;
    return c;
  }, [talents]);

  const visible = useMemo(() => {
    if (filter === 'all') return talents;
    return talents.filter((t) => t.category === filter);
  }, [filter, talents]);

  // Stats
  const featuredCount = useMemo(() => talents.filter((t) => t.featured).length, [talents]);
  const categoryCount = useMemo(() => Object.keys(counts).filter((k) => k !== 'all' && counts[k] > 0).length, [counts]);

  const imgFor = (t) => t.imageUrl || `https://picsum.photos/seed/${t.imageSeed || `cb-talent-${t.id}`}/900/1100`;

  return (
    <main className="tal-page">
      {/* ============== HERO ============== */}
      <header className="tal-hero">
        <div className="tal-hero-bg" />
        <div className="tal-hero-grain" />
        <div className="tal-hero-watermark" aria-hidden="true">Visages</div>

        <div className="wrap tal-hero-inner reveal">
          <span className="tal-hero-eyebrow">Talents · Personnalités · 2026</span>
          <h1>
            Visages<br />
            <span className="ital">de Cambérène.</span>
          </h1>
          <p className="lede">
            Artistes, sportifs, entrepreneurs, créateurs, aînés — celles et ceux qui portent
            le village au-delà de ses dunes. Six catégories, autant de manières d'incarner Cambérène.
          </p>

          <div className="tal-hero-foot">
            <div><b>{loading ? '—' : talents.length}</b><span>Portraits publiés</span></div>
            <div><b>{featuredCount}</b><span>Mis en avant</span></div>
            <div><b>{categoryCount}</b><span>Catégories</span></div>
            <div><b>{new Set(talents.map((t) => t.location).filter(Boolean)).size}</b><span>Lieux du village</span></div>
          </div>
        </div>
      </header>

      {/* ============== FILTRES ============== */}
      <div className="tal-filters">
        <div className="wrap">
          <span className="lab">Catégories —</span>
          {TALENT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`tal-chip${filter === c.id ? ' on' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.lbl}
              <span className="ct">{counts[c.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ============== GRID ============== */}
      <section className="tal-section">
        <div className="wrap">
          <div className="tal-head reveal">
            <div>
              <span className="eyebrow">{visible.length} portrait{visible.length > 1 ? 's' : ''}</span>
              <h2>Les visages<br /><span className="ital">du village.</span></h2>
            </div>
          </div>

          {loading && (
            <p style={{ textAlign: 'center', padding: '60px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement des portraits…
            </p>
          )}

          {!loading && visible.length === 0 && (
            <p style={{ textAlign: 'center', padding: '60px 0', color: 'var(--char-500)' }}>
              Aucun portrait pour cette catégorie.
            </p>
          )}

          {!loading && visible.length > 0 && (
            <div className="tal-grid">
              {visible.map((t) => (
                <article key={t.id} className="tal-card reveal">
                  <div className="photo">
                    <div className="img" style={{ backgroundImage: `url('${imgFor(t)}')` }} />
                    {t.role && <span className="role">{t.role}</span>}
                    {t.featured && <span className="star" title="Mis en avant">★</span>}
                  </div>
                  <div className="body">
                    <span className="cat-mini">{CATEGORY_LABEL[t.category] || t.category}</span>
                    <h4>{t.fullName}</h4>
                    {t.location && <span className="from">{t.location}</span>}
                    {t.description && <p className="desc">{t.description}</p>}
                    <div className="foot">
                      {t.socials && t.socials.length > 0 ? (
                        <div className="socials">
                          {t.socials.slice(0, 4).map((s, i) => (
                            <a
                              key={i}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={s.type}
                            >
                              {SOCIAL_SHORT[s.type] || s.type.toUpperCase()}
                            </a>
                          ))}
                        </div>
                      ) : <span />}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="tal-cta">
        <div className="wrap reveal">
          <span className="eyebrow" style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Vous connaissez un talent ?
          </span>
          <h3>Suggérez un nouveau <span className="ital">visage.</span></h3>
          <p>
            Les portraits sont validés par la communauté. Si vous connaissez un habitant ou natif
            de Cambérène qui mériterait d'être mis en avant, envoyez-nous son nom et quelques lignes.
          </p>
          <div className="tal-cta-actions">
            <Link to="/inscription" className="btn btn-primary">
              <span>Proposer un portrait</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/asc" className="btn btn-outline">
              <span>Sportifs · ASC</span>
              <span className="arrow">↗</span>
            </Link>
            <Link to="/annuaire" className="btn btn-outline">
              <span>Annuaire des commerces</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TourismeCulturel;
